import { useCallback } from 'react';
import { ResourceType } from 'api/resource-management/types';
import {
  SchedulerResourceType,
  TeamAsLeafResource,
  UserAsLeafResource,
} from 'components/organisms/resource-management/resource-types';
import { SchedulerReference } from 'components/organisms/resource-management/types';
import {
  TemplateAllocation as Allocation,
  useGetAllAllocations,
} from 'data/templates/projects';
import { totalMinutesFromAllocations } from '../utils/total-allocations';

interface Props {
  schedulerRef: SchedulerReference;
}

export const useAggregationManager = ({
  schedulerRef,
}: Props): {
  refreshAggregation: (allocations?: Allocation[]) => void;
} => {
  const allAllocations = useGetAllAllocations();

  const refreshAggregation = useCallback(
    (allocations = Object.values(allAllocations)) => {
      const resources =
        schedulerRef.current.instance.project.resourceStore.data;

      resources.forEach(({ id, children }) => {
        const resource =
          schedulerRef.current.instance.project.resourceStore.getById(id);
        const totalMinutes = totalMinutesFromAllocations(allocations);

        if (resource != null) {
          resource.set('totalAllocation', totalMinutes);
        }

        children.forEach(({ type, children: roleChildren }) => {
          if (type !== SchedulerResourceType.Role) {
            return;
          }
          (
            (roleChildren ?? []) as (UserAsLeafResource | TeamAsLeafResource)[]
          ).forEach((userOrTeamResource) => {
            const leafResource =
              schedulerRef.current.instance.project.resourceStore.getById(
                userOrTeamResource.id,
              );

            if (leafResource != null) {
              leafResource.set(
                'totalAllocation',
                userOrTeamResource.type === SchedulerResourceType.User
                  ? totalMinutesFromAllocations(
                      allocations.filter(
                        (x) =>
                          x.type === ResourceType.User &&
                          x.userId === userOrTeamResource.userId,
                      ),
                    )
                  : totalMinutesFromAllocations(
                      allocations.filter(
                        (x) =>
                          x.type === ResourceType.Team &&
                          x.teamId === userOrTeamResource.teamId,
                      ),
                    ),
              );
            }
          });
        });
      });
    },
    [allAllocations, schedulerRef],
  );

  return {
    refreshAggregation,
  };
};
